// src/views/menage/MenageAvisIndicator.jsx — CHANTIER AVIS MÉNAGE/ORCH Lot N3
//
// Bloc INDICATEUR d'avis voyageurs, AJOUTÉ EN BAS des deux dashboards
// (MenageTodayView = agent · MenageSuiviView = orchestrateur). Composant UNIFIÉ :
// le NOMBRE de logements du périmètre décide du rendu (switch ‹ › si > 1, simple
// si = 1), PAS le rôle. Consomme l'endpoint LECTURE SEULE `menage_avis` via
// fetchMenageAvis (scope + anti-PII garantis CÔTÉ SERVEUR, Lot N1) :
//   - 0 montant, 0 nom voyageur (whitelist serveur SANS guest_name) ;
//   - met en avant la note de PROPRETÉ (category_ratings.cleanliness) = « le
//     reflet du travail » de l'agent ;
//   - affiche la note moyenne + le dernier avis (texte + date + source).
//
// Carte CLIQUABLE → onglet « Avis » via onNavigate('menage-avis') (onglet créé
// par le Lot N2). Si onNavigate n'est pas câblé (App.jsx pas encore branché),
// la carte reste inerte (jamais de crash). États loading / vide / erreur
// gracieux : jamais de crash si 0 avis.
//
// 375px : le bloc s'empile sous le contenu existant ; les flèches sont tactiles
// (cibles ≥ 40px) ; les libellés tronquent au lieu de déborder.

import { useState, useEffect, useMemo, useCallback } from "react"
import { DS } from "../../lib/ui"
import { formatDateFR } from "../../lib/format"
import { fetchMenageAvis } from "../../lib/menage-avis-api"
// Icônes trait fin (lucide-react direct — DESIGN.md §5).
import { Star, ChevronLeft, ChevronRight, MessageSquareQuote, ArrowRight } from "lucide-react"

// Arrondi 1 décimale (identique au calcul serveur des stats).
const round1 = (x) => Math.round(x * 10) / 10

// Note de propreté d'un avis (category_ratings.cleanliness) → nombre fini | null.
function cleanlinessOf(avis) {
  const c = avis?.category_ratings?.cleanliness
  const n = c == null ? NaN : Number(c)
  return Number.isFinite(n) ? n : null
}

// Construit les cartes-logement à partir du payload `menage_avis`
// ({ avis, statsByLog, statsOverall }). Helper PUR (exporté pour les tests).
//   - 1 carte par clé de statsByLog (= le NOMBRE de logements du périmètre) ;
//   - `avg`/`count` = stats serveur DGCCRF du logement ;
//   - `cleanliness` = MOYENNE des notes de propreté du logement (le reflet du
//     travail, plus stable qu'un seul avis) ;
//   - `lastAvis` = dernier avis du logement (l'API trie déjà date desc, id desc :
//     le 1er avis rencontré pour un logement EST le plus récent) ;
//   - TRI : le PLUS MAUVAIS dernier avis EN PREMIER (note globale du dernier
//     avis, croissant) ; un dernier avis sans note globale est renvoyé en fin
//     (on ne peut pas le qualifier de « mauvais »).
export function buildAvisIndicatorCards(payload) {
  const avis = Array.isArray(payload?.avis) ? payload.avis : []
  const statsByLog =
    payload?.statsByLog && typeof payload.statsByLog === "object" ? payload.statsByLog : {}

  const latestByLog = {}
  const cleanAccByLog = {}
  for (const a of avis) {
    const lid = a?.logement_id
    if (!lid) continue
    if (!(lid in latestByLog)) latestByLog[lid] = a // 1er rencontré = plus récent
    const cl = cleanlinessOf(a)
    if (cl != null) {
      const acc = cleanAccByLog[lid] || (cleanAccByLog[lid] = { sum: 0, n: 0 })
      acc.sum += cl
      acc.n += 1
    }
  }

  const cards = Object.keys(statsByLog).map((lid) => {
    const acc = cleanAccByLog[lid]
    const s = statsByLog[lid] || {}
    return {
      logementId: lid,
      count: typeof s.count === "number" ? s.count : 0,
      avg: s.avg == null ? null : Number(s.avg),
      cleanliness: acc && acc.n > 0 ? round1(acc.sum / acc.n) : null,
      lastAvis: latestByLog[lid] || null,
    }
  })

  cards.sort((a, b) => {
    const ra = a.lastAvis?.rating == null ? Infinity : Number(a.lastAvis.rating)
    const rb = b.lastAvis?.rating == null ? Infinity : Number(b.lastAvis.rating)
    if (ra !== rb) return ra - rb
    return String(a.logementId).localeCompare(String(b.logementId))
  })

  return cards
}

// Libellé court d'un logement depuis cfg (logs + chambres sous-loc). Fallback =
// l'id en MAJUSCULES (jamais de blanc). Aligné sur findLog des deux dashboards.
function resolveLogLabel(cfg, logementId) {
  if (cfg && Array.isArray(cfg.props)) {
    for (const prop of cfg.props) {
      for (const log of prop.logs || []) {
        if (log.id === logementId) return log.nom || log.id
        for (const ch of log.chambres || []) {
          if (ch.id === logementId) return ch.nom || ch.id
        }
      }
    }
  }
  return String(logementId || "").toUpperCase() || "Logement"
}

// Libellé source d'avis (jamais de PII). Capitalise les sources inconnues.
const SOURCE_LABELS = { airbnb: "Airbnb", booking: "Booking", direct: "Direct", google: "Google" }
function sourceLabel(src) {
  if (!src) return null
  const key = String(src).toLowerCase()
  if (SOURCE_LABELS[key]) return SOURCE_LABELS[key]
  return key.charAt(0).toUpperCase() + key.slice(1)
}

// Affichage d'une note /5 (1 décimale) ou tiret si absente.
function ratingText(v) {
  return v == null || !Number.isFinite(Number(v)) ? "—" : `${round1(Number(v))}/5`
}

// Pastille note (étoile + valeur). `hero` = grande mise en avant propreté.
function RatingPill({ value, label, hero }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <Star size={hero ? 18 : 14} strokeWidth={2} color={DS.warning} fill={DS.warning} style={{ flexShrink: 0 }} />
        <span style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: hero ? 26 : 16, lineHeight: 1, color: DS.marine }}>
          {ratingText(value)}
        </span>
      </div>
      <span style={{ fontSize: hero ? 11.5 : 10.5, color: DS.textMuted, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
    </div>
  )
}

export default function MenageAvisIndicator({ currentUser, cfg, onNavigate }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [cards, setCards] = useState([])
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    ;(async () => {
      try {
        const payload = await fetchMenageAvis()
        if (cancelled) return
        setCards(buildAvisIndicatorCards(payload))
        setIdx(0)
      } catch (e) {
        if (cancelled) return
        // Jamais de PII dans le log ; l'erreur dégrade en bloc « indisponible ».
        console.error("[menage-avis-indicator] chargement avis échoué")
        setError(true)
        setCards([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [currentUser?.email])

  // Index borné (un changement de périmètre peut réduire le nombre de cartes).
  const safeIdx = cards.length ? Math.min(idx, cards.length - 1) : 0
  const card = cards[safeIdx] || null
  const multi = cards.length > 1

  const go = useCallback((delta) => {
    setIdx((prev) => {
      const n = cards.length
      if (!n) return 0
      return (((prev + delta) % n) + n) % n
    })
  }, [cards.length])

  const logLabel = useMemo(
    () => (card ? resolveLogLabel(cfg, card.logementId) : ""),
    [cfg, card]
  )

  const navigate = useCallback(() => {
    if (typeof onNavigate === "function") onNavigate("menage-avis")
  }, [onNavigate])

  // ── En-tête de section (toujours rendu : repère stable en bas de page) ──
  const SectionHeader = (
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
      <span style={{ width: 30, height: 30, borderRadius: 9, background: DS.cielSoft, color: DS.marine, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <MessageSquareQuote size={16} strokeWidth={2} />
      </span>
      <div style={{ fontFamily: DS.fontDisplay, fontSize: 15, fontWeight: 600, color: DS.marineDark }}>
        Vos avis voyageurs
      </div>
    </div>
  )

  const wrapStyle = { marginTop: 28, paddingTop: 4, fontFamily: DS.fontBody }

  if (loading) {
    return (
      <div style={wrapStyle}>
        {SectionHeader}
        <div style={{ padding: 20, textAlign: "center", color: DS.textMuted, fontSize: 13, background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 14 }}>
          Chargement de vos avis…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={wrapStyle}>
        {SectionHeader}
        <div style={{ padding: 18, textAlign: "center", color: DS.textMuted, fontSize: 12.5, background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 14 }}>
          Avis momentanément indisponibles.
        </div>
      </div>
    )
  }

  if (!card) {
    return (
      <div style={wrapStyle}>
        {SectionHeader}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "28px 20px", textAlign: "center", color: DS.textSecondary, fontSize: 13, background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 14 }}>
          <span style={{ width: 42, height: 42, borderRadius: 12, background: DS.cielSoft, color: DS.marine, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Star size={20} strokeWidth={2} />
          </span>
          Pas encore d'avis pour vos logements.
        </div>
      </div>
    )
  }

  const last = card.lastAvis
  const src = sourceLabel(last?.source)
  const reviewText = (last?.review_text || "").trim()

  return (
    <div style={wrapStyle}>
      {SectionHeader}

      <div
        role="button"
        tabIndex={0}
        onClick={navigate}
        onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate() } }}
        className="fbz-press"
        style={{
          background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 16,
          boxShadow: "0 1px 2px rgba(33,83,135,.05)", padding: "16px 16px 14px",
          cursor: typeof onNavigate === "function" ? "pointer" : "default", textAlign: "left",
        }}
      >
        {/* Ligne logement + switch (uniquement si > 1 logement). */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ flex: 1, minWidth: 0, fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 15, color: DS.marine, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {logLabel}
          </span>
          {multi && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
              <button
                type="button" aria-label="Logement précédent"
                onClick={(e) => { e.stopPropagation(); go(-1) }}
                className="fbz-press"
                style={{ width: 40, height: 40, display: "inline-flex", alignItems: "center", justifyContent: "center", background: DS.toolBg, border: `0.5px solid ${DS.border}`, borderRadius: 10, cursor: "pointer", color: DS.marine }}
              >
                <ChevronLeft size={18} strokeWidth={2.2} />
              </button>
              <span style={{ minWidth: 38, textAlign: "center", fontSize: 11.5, fontWeight: 700, color: DS.textSecondary }}>
                {safeIdx + 1}/{cards.length}
              </span>
              <button
                type="button" aria-label="Logement suivant"
                onClick={(e) => { e.stopPropagation(); go(1) }}
                className="fbz-press"
                style={{ width: 40, height: 40, display: "inline-flex", alignItems: "center", justifyContent: "center", background: DS.toolBg, border: `0.5px solid ${DS.border}`, borderRadius: 10, cursor: "pointer", color: DS.marine }}
              >
                <ChevronRight size={18} strokeWidth={2.2} />
              </button>
            </div>
          )}
        </div>

        {/* Notes : PROPRETÉ mise en avant (le reflet du travail) + moyenne. */}
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", padding: "12px 14px", background: DS.cielSoft, borderRadius: 12, marginBottom: 12 }}>
          <RatingPill value={card.cleanliness} label="Propreté — le reflet de votre travail" hero />
          <RatingPill value={card.avg} label={`Note moyenne · ${card.count} avis`} />
        </div>

        {/* Dernier avis : texte (sans nom voyageur) + date + source. */}
        {last ? (
          <div style={{ borderLeft: `3px solid ${DS.ciel}`, paddingLeft: 12 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: DS.textMuted, marginBottom: 4 }}>
              Dernier avis
            </div>
            <div style={{ fontSize: 13, color: DS.textPrimary, lineHeight: 1.5, fontStyle: reviewText ? "italic" : "normal" }}>
              {reviewText ? `« ${reviewText} »` : "Avis sans commentaire."}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 6, fontSize: 11.5, color: DS.textMuted, flexWrap: "wrap" }}>
              <span>{formatDateFR(last.review_date)}</span>
              {src && <><span aria-hidden>·</span><span>{src}</span></>}
              {last.rating != null && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <span aria-hidden>·</span>
                  <Star size={12} strokeWidth={2} color={DS.warning} fill={DS.warning} /> {ratingText(last.rating)}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: DS.textMuted }}>Pas encore de commentaire.</div>
        )}

        {/* Affordance « voir tous les avis » (seulement si la nav est câblée). */}
        {typeof onNavigate === "function" && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, marginTop: 12, fontSize: 12, fontWeight: 700, color: DS.corail }}>
            Voir tous les avis <ArrowRight size={15} strokeWidth={2.4} />
          </div>
        )}
      </div>
    </div>
  )
}
